import axios from "axios";
import { getCookie } from "../utils/cookie";
import {
  getRefreshToken,
  clearAuthCookies,
  hasRefreshableSession,
} from "../auth/session";
import {
  getExternalSession,
  adoptExternalSession,
  clearExternalSession,
  listExternalSessions,
} from "../auth/externalSession";
import apiRoutes from "./api_route";
import logError from "../utils/logError";
import { encryptPayload, decryptPayload } from "../utils/payloadCrypto";

let isRefreshing = false;
let failedQueue = [];

// External (participant) token refresh is single-flight per process: only one
// POST /video/token/refresh runs at a time and every 401 that raced it waits
// on the same promise so they all retry with the rotated access token.
let isExternalRefreshing = false;
let externalFailedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach((promise) => {
    if (error) promise.reject(error);
    else promise.resolve();
  });
  failedQueue = [];
};

const processExternalQueue = (error) => {
  externalFailedQueue.forEach((promise) => {
    if (error) promise.reject(error);
    else promise.resolve();
  });
  externalFailedQueue = [];
};

const api = axios.create({
  baseURL: "",
  headers: { "Content-Type": "application/json" },
});

// Rotate one meeting's external token pair and persist it. Throws when the
// refresh itself is rejected (stale/revoked session) — callers decide whether
// that is fatal.
async function refreshExternalSession(meetingCode) {
  const session = getExternalSession(meetingCode);
  if (!session || !session.refreshToken) return null;
  try {
    const response = await api.post(
      apiRoutes.token.refresh,
      { refreshExternalToken: session.refreshToken },
      { _isExternalRefresh: true, meetingCode },
    );
    const data = response.data;
    if (!data || !data.token) throw new Error("Empty refresh response");
    const updated = adoptExternalSession({
      meetingCode,
      token: data.token,
      refreshExternalToken: data.refreshExternalToken,
      identity: session.identity,
      phoneNumber: session.phoneNumber,
      meetingId: session.meetingId,
      useParticipantAuth: session.useParticipantAuth,
    });
    return updated ? updated.accessToken : null;
  } catch (err) {
    clearExternalSession(meetingCode);
    throw err;
  }
}

// Time-based rotation: refresh sessions that are about to expire so a long
// lobby stay (or an idle but connected guest) never 401s mid-meeting.
const EXTERNAL_PRE_EXPIRY_MS = 60 * 1000;
setTimeout(() => {
  setInterval(() => {
    const now = Date.now();
    listExternalSessions().forEach((session) => {
      if (
        !session.accessExpiresAt ||
        session.accessExpiresAt - now > EXTERNAL_PRE_EXPIRY_MS
      ) {
        return;
      }
      refreshExternalSession(session.meetingCode).catch((err) => {
        logError("external-refresh", err);
      });
    });
  }, 15 * 1000);
}, 1500);

api.interceptors.request.use((config) => {
  // A meeting-scoped external token wins over the session cookie. The backend's
  // externalTokenVerify reads `Authorization: Bearer <externalToken>` and sets
  // req.externalUser from it; POST /video/token/ is host-gated on that, not on
  // the login JWT. Without this the cookie below used to overwrite the header
  // and every /video/token/ call came back 401.
  if (config.externalToken) {
    config.headers.Authorization = `Bearer ${config.externalToken}`;
  } else {
    const token = getCookie("accessToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  // Auto-tag the meeting so a 401 on any room-scoped call can route to the
  // participant session refresh (status/sync/approve share the same code).
  if (!config.meetingCode) {
    const candidate =
      config.data?.meetingCode ||
      config.data?.roomCode ||
      config.params?.meetingCode ||
      config.params?.roomCode ||
      null;
    if (candidate) config.meetingCode = String(candidate);
  }
  if (config.data && typeof config.data === "object") {
    config.data = encryptPayload(config.data);
  }
  return config;
});

api.interceptors.response.use(
  (res) => {
    if (res.data && typeof res.data === "object" && res.data.payload) {
      res.data = decryptPayload(res.data);
    }
    return res;
  },
  async (error) => {
    if (
      error.response?.data &&
      typeof error.response.data === "object" &&
      error.response.data.payload
    ) {
      error.response.data = decryptPayload(error.response.data);
      error.response.data = error.response.data;
    }
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      hasRefreshableSession()
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => api(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newAccessToken = await getRefreshToken();

        if (newAccessToken) {
          processQueue(null);
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        }

        const err = new Error("Refresh token expired");
        processQueue(err);
        clearAuthCookies();
      } catch (refreshError) {
        processQueue(refreshError);
        clearAuthCookies();
      } finally {
        isRefreshing = false;
      }
    }

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest._isExternalRefresh &&
      originalRequest.meetingCode &&
      getExternalSession(originalRequest.meetingCode) &&
      !hasRefreshableSession()
    ) {
      if (isExternalRefreshing) {
        return new Promise((resolve, reject) => {
          externalFailedQueue.push({ resolve, reject });
        })
          .then(() => api(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isExternalRefreshing = true;

      try {
        const newAccessToken = await refreshExternalSession(
          originalRequest.meetingCode,
        );

        if (newAccessToken) {
          processExternalQueue(null);
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        }

        processExternalQueue(new Error("External session ended"));
      } catch (refreshError) {
        processExternalQueue(refreshError);
      } finally {
        isExternalRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export { refreshExternalSession as refreshExternalSessionApi };
export default api;
